import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router';
import { MUNICIPALITIES, PASSWORD_MIN_LENGTH, SCHOOL_FORMS } from '../../../shared/constants.ts';
import { api, type User } from '../api.ts';
import { useAuth } from '../auth.tsx';
import { Container } from '../components/Layout.tsx';
import {
  Alert,
  Button,
  Card,
  ErrorMessage,
  Field,
  Loading,
  PageTitle,
  SelectField,
} from '../components/ui.tsx';
import { useAction, useTitle } from '../hooks.ts';

interface RegistrationInfo {
  restricted: boolean;
  allowedDomains: string[];
  invite: { valid: boolean; municipality: string | null; label: string } | null;
}

function safeRedirect(target: string | null): string {
  return target && target.startsWith('/') && !target.startsWith('//') ? target : '/kurs';
}

export function LoginPage() {
  useTitle('Logga in');
  const { user, setUser } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [pending, setPending] = useState<string | null>(null);
  const [mode, setMode] = useState<'password' | 'link'>('password');
  const [linkSent, setLinkSent] = useState(false);
  const login = useAction(() =>
    api.post<{ user?: User; twoFactorRequired?: boolean; token?: string }>('/api/auth/login', {
      email,
      password,
    }),
  );
  const sendLink = useAction(() => api.post('/api/auth/login-link', { email }));
  if (user) return <Navigate to={safeRedirect(params.get('till'))} replace />;

  const done = (u: User) => {
    setUser(u);
    navigate(safeRedirect(params.get('till')), { replace: true });
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (mode === 'link') {
      if (await sendLink.run()) setLinkSent(true);
      return;
    }
    const res = await login.run();
    if (res?.twoFactorRequired && res.token) setPending(res.token);
    else if (res?.user) done(res.user);
  };

  if (pending) return <TwoFactorStep token={pending} onDone={done} onBack={() => setPending(null)} />;

  return (
    <Container narrow>
      <PageTitle>Logga in</PageTitle>
      <Card>
        {linkSent ? (
          <div className="space-y-4">
            <Alert kind="success">
              Om det finns ett konto för {email} har vi skickat en inloggningslänk. Den gäller i 15 minuter.
            </Alert>
            <button
              type="button"
              className="text-sm text-night-700 underline"
              onClick={() => {
                setLinkSent(false);
                setMode('password');
              }}
            >
              Logga in med lösenord i stället
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4" noValidate>
            <ErrorMessage error={mode === 'link' ? sendLink.error : login.error} />
            <Field
              label="E-postadress"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            {mode === 'password' ? (
              <Field
                label="Lösenord"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            ) : (
              <p className="text-sm text-muted">
                Vi skickar en länk till din e-post. Klicka på den så är du inloggad.
              </p>
            )}
            <Button
              type="submit"
              pending={mode === 'link' ? sendLink.pending : login.pending}
              className="w-full"
            >
              {mode === 'link' ? 'Skicka inloggningslänk' : 'Logga in'}
            </Button>
            <button
              type="button"
              className="w-full text-center text-sm text-night-700 underline"
              onClick={() => setMode(mode === 'link' ? 'password' : 'link')}
            >
              {mode === 'link'
                ? 'Logga in med lösenord i stället'
                : 'Logga in med en länk via e-post i stället'}
            </button>
            <div className="flex justify-between text-sm">
              <Link to="/glomt-losenord" className="text-night-700 underline">
                Glömt lösenordet?
              </Link>
              <Link to="/registrera" className="text-night-700 underline">
                Skapa konto
              </Link>
            </div>
          </form>
        )}
      </Card>
    </Container>
  );
}

/** Andra steget i inloggningen för den som har tvåstegsverifiering. */
function TwoFactorStep({
  token,
  onDone,
  onBack,
}: {
  token: string;
  onDone: (user: User) => void;
  onBack: () => void;
}) {
  const [code, setCode] = useState('');
  const second = useAction(() => api.post<{ user: User }>('/api/auth/login/2fa', { token, code }));
  const submitCode = async (e: FormEvent) => {
    e.preventDefault();
    const res = await second.run();
    if (res) onDone(res.user);
    else if (second.error?.code === 'login_expired') onBack();
  };
  return (
    <Container narrow>
      <PageTitle>Tvåstegsverifiering</PageTitle>
      <Card>
        <form onSubmit={submitCode} className="space-y-4" noValidate>
          <p>Öppna appen i telefonen och skriv in den sexsiffriga koden för Aurora.</p>
          {second.error && !second.error.fields.code && <ErrorMessage error={second.error} />}
          <Field
            label="Kod"
            inputMode="numeric"
            autoComplete="one-time-code"
            autoFocus
            required
            value={code}
            onChange={(e) => setCode(e.target.value)}
            error={second.error?.fields.code}
            hint="Har du inte telefonen? Skriv en av dina reservkoder i stället."
          />
          <Button type="submit" pending={second.pending} disabled={code.trim().length < 6} className="w-full">
            Logga in
          </Button>
          <button type="button" className="text-sm text-night-700 underline" onClick={onBack}>
            Tillbaka
          </button>
        </form>
      </Card>
    </Container>
  );
}

/** Länken från mejlet "Logga in i Aurora". */
export function LinkLoginPage() {
  useTitle('Logga in');
  const [params] = useSearchParams();
  const token = params.get('token');
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState<{ status: 'idle' | '2fa' | 'error'; message?: string; token?: string }>({
    status: 'idle',
  });
  const started = useRef(false);
  const done = (u: User) => {
    setUser(u);
    navigate('/kurs', { replace: true });
  };
  useEffect(() => {
    if (!token || started.current) return;
    started.current = true;
    api
      .post<{ user?: User; twoFactorRequired?: boolean; token?: string }>('/api/auth/login-link/verify', {
        token,
      })
      .then((res) => {
        if (res.twoFactorRequired && res.token) setState({ status: '2fa', token: res.token });
        else if (res.user) done(res.user);
      })
      .catch((e) => setState({ status: 'error', message: e.message }));
  }, [token]);
  if (state.status === '2fa' && state.token)
    return <TwoFactorStep token={state.token} onDone={done} onBack={() => navigate('/logga-in')} />;
  return (
    <Container narrow>
      <PageTitle>Logga in</PageTitle>
      <Card className="space-y-4">
        {!token && <Alert>Länken saknar kod.</Alert>}
        {token && state.status === 'idle' && <Loading />}
        {state.status === 'error' && (
          <>
            <Alert>{state.message}</Alert>
            <Link to="/logga-in" className="inline-block font-semibold text-night-700 underline">
              Till inloggningen
            </Link>
          </>
        )}
      </Card>
    </Container>
  );
}

export function RegisterPage() {
  useTitle('Skapa konto');
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [form, setForm] = useState({
    inviteCode: params.get('kod') ?? '',
    joinToken: params.get('kurs') ?? '',
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    municipality: '',
    school: '',
    schoolForm: '',
    subject: '',
    acceptTerms: false,
  });
  const set = (key: keyof typeof form) => (e: { target: { value: string } }) =>
    setForm({ ...form, [key]: e.target.value });
  const register = useAction(() =>
    api.post<{ user: User }>('/api/auth/register', { ...form, joinToken: form.joinToken || undefined }),
  );
  const [policy, setPolicy] = useState<RegistrationInfo | null>(null);
  const [showCode, setShowCode] = useState(!!form.inviteCode);
  // Koden kontrolleras medan den skrivs, så att kommunen kan fyllas i direkt.
  useEffect(() => {
    const code = form.inviteCode.trim();
    const timer = window.setTimeout(
      () => {
        api
          .get<RegistrationInfo>(`/api/auth/registration${code ? `?code=${encodeURIComponent(code)}` : ''}`)
          .then((info) => {
            setPolicy(info);
            if (info.restricted) setShowCode(true);
            const municipality = info.invite?.municipality;
            if (municipality) setForm((f) => ({ ...f, municipality }));
          })
          .catch(() => undefined);
      },
      code ? 400 : 0,
    );
    return () => window.clearTimeout(timer);
  }, [form.inviteCode]);
  if (user) return <Navigate to="/kurs" replace />;
  const f = register.error?.fields ?? {};
  const invite = policy?.invite;
  const lockedMunicipality = invite?.valid ? invite.municipality : null;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const res = await register.run();
    if (res) {
      setUser(res.user);
      navigate('/verifiera-epost', { replace: true });
    }
  };

  return (
    <Container>
      <div className="mx-auto max-w-2xl">
        <PageTitle sub="Kontot är personligt. Uppgifterna om kommun och skola används för att följa upp kursen i länet.">
          Skapa konto
        </PageTitle>
        <Card>
          <form onSubmit={submit} className="space-y-5" noValidate>
            {register.error && !Object.keys(f).length && <ErrorMessage error={register.error} />}
            {Object.keys(f).length > 0 && <Alert>{register.error?.message}</Alert>}
            {form.joinToken && (
              <Alert kind="info">Du anmäls till kurstillfället från länken när kontot är skapat.</Alert>
            )}
            {policy?.restricted && !form.joinToken && (
              <Alert kind="info">
                {policy.allowedDomains.length ? (
                  <>
                    Registrera dig med din jobbadress ({policy.allowedDomains.join(', ')}) eller med
                    inbjudningskoden du har fått.
                  </>
                ) : (
                  <>
                    Du behöver en inbjudningskod för att skapa konto. Fråga din skolledare eller
                    kommunkontakt.
                  </>
                )}
              </Alert>
            )}
            {showCode ? (
              <Field
                label={policy?.restricted ? 'Inbjudningskod' : 'Inbjudningskod (valfritt)'}
                value={form.inviteCode}
                onChange={set('inviteCode')}
                error={
                  f.inviteCode ??
                  (invite && !invite.valid && form.inviteCode.trim() ? 'Koden gäller inte.' : undefined)
                }
                hint={
                  invite?.valid
                    ? `Koden gäller${invite.municipality ? ` för ${invite.municipality}` : ''}${invite.label ? ` (${invite.label})` : ''}.`
                    : undefined
                }
                autoComplete="off"
              />
            ) : (
              <button
                type="button"
                className="text-sm text-night-700 underline"
                onClick={() => setShowCode(true)}
              >
                Jag har en inbjudningskod
              </button>
            )}
            <div className="grid gap-4 sm:grid-cols-2">
              <Field
                label="Förnamn"
                autoComplete="given-name"
                value={form.firstName}
                onChange={set('firstName')}
                error={f.firstName}
                required
              />
              <Field
                label="Efternamn"
                autoComplete="family-name"
                value={form.lastName}
                onChange={set('lastName')}
                error={f.lastName}
                required
              />
            </div>
            <Field
              label="E-postadress"
              type="email"
              autoComplete="email"
              value={form.email}
              onChange={set('email')}
              error={f.email}
              hint="Gärna din jobbadress."
              required
            />
            <Field
              label="Lösenord"
              type="password"
              autoComplete="new-password"
              value={form.password}
              onChange={set('password')}
              error={f.password}
              hint={`Minst ${PASSWORD_MIN_LENGTH} tecken. En mening eller flera ord är lätt att komma ihåg.`}
              required
            />
            <div className="grid gap-4 sm:grid-cols-2">
              <SelectField
                label="Kommun"
                value={form.municipality}
                onChange={set('municipality')}
                error={f.municipality}
                disabled={!!lockedMunicipality}
                hint={lockedMunicipality ? 'Kommunen kommer från inbjudningskoden.' : undefined}
                options={[
                  { value: '', label: 'Välj kommun' },
                  ...MUNICIPALITIES.map((m) => ({ value: m, label: m })),
                ]}
                required
              />
              <SelectField
                label="Skolform"
                value={form.schoolForm}
                onChange={set('schoolForm')}
                error={f.schoolForm}
                options={[
                  { value: '', label: 'Välj skolform' },
                  ...SCHOOL_FORMS.map((m) => ({ value: m, label: m })),
                ]}
                required
              />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field
                label="Skola (valfritt)"
                autoComplete="organization"
                value={form.school}
                onChange={set('school')}
                error={f.school}
              />
              <Field
                label="Ämne eller roll (valfritt)"
                value={form.subject}
                onChange={set('subject')}
                error={f.subject}
              />
            </div>
            <label className="flex items-start gap-3 text-sm">
              <input
                type="checkbox"
                className="mt-1 h-4 w-4 accent-night-800"
                checked={form.acceptTerms}
                onChange={(e) => setForm({ ...form, acceptTerms: e.target.checked })}
              />
              <span>
                Jag har läst hur{' '}
                <Link to="/integritet" target="_blank" className="text-night-700 underline">
                  mina personuppgifter hanteras
                </Link>{' '}
                och godkänner det.
                {f.acceptTerms && <span className="block text-red-700">{f.acceptTerms}</span>}
              </span>
            </label>
            <Button type="submit" pending={register.pending} className="w-full sm:w-auto">
              Skapa konto
            </Button>
          </form>
        </Card>
      </div>
    </Container>
  );
}

export function VerifyEmailPage() {
  useTitle('Bekräfta e-post');
  const [params] = useSearchParams();
  const token = params.get('token');
  const { user, refresh, loading } = useAuth();
  const [status, setStatus] = useState<'idle' | 'ok' | 'error'>('idle');
  const [message, setMessage] = useState('');
  const started = useRef(false);
  const resend = useAction(() => api.post('/api/auth/resend-verification'));
  const [resent, setResent] = useState(false);

  useEffect(() => {
    if (!token || started.current) return;
    started.current = true;
    api
      .post('/api/auth/verify-email', { token })
      .then(async () => {
        setStatus('ok');
        await refresh();
      })
      .catch((e) => {
        setStatus('error');
        setMessage(e.message);
      });
  }, [token, refresh]);

  if (loading) return <Loading />;

  return (
    <Container narrow>
      <PageTitle>Bekräfta din e-postadress</PageTitle>
      <Card className="space-y-4">
        {token && status === 'idle' && <Loading />}
        {status === 'ok' && (
          <>
            <Alert kind="success">Tack! Din e-postadress är bekräftad.</Alert>
            <Link
              to={user ? '/kurs' : '/logga-in'}
              className="inline-block font-semibold text-night-700 underline"
            >
              {user ? 'Gå till kursen' : 'Logga in'}
            </Link>
          </>
        )}
        {status === 'error' && <Alert>{message}</Alert>}
        {!token && user && !user.emailVerified && (
          <>
            <p>
              Vi har skickat ett mejl till <strong>{user.email}</strong> med en länk. Klicka på länken för att
              komma igång med kursen. Titta i skräpposten om du inte hittar mejlet.
            </p>
            {resent ? (
              <Alert kind="success">Ett nytt mejl är på väg.</Alert>
            ) : (
              <Button
                variant="secondary"
                pending={resend.pending}
                onClick={async () => {
                  if (await resend.run()) setResent(true);
                }}
              >
                Skicka länken igen
              </Button>
            )}
            <ErrorMessage error={resend.error} />
            <FixEmail onChanged={() => setResent(true)} />
          </>
        )}
        {!token && user?.emailVerified && <Navigate to="/kurs" replace />}
        {!token && !user && <Navigate to="/logga-in" replace />}
      </Card>
    </Container>
  );
}

/** För den som skrev fel adress vid registreringen: rätta den och få en ny länk direkt. */
function FixEmail({ onChanged }: { onChanged: () => void }) {
  const { user, setUser, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState(user?.email ?? '');
  const change = useAction(() => api.post<{ user: User }>('/api/auth/change-email', { email }));
  return (
    <div className="border-t border-night-100 pt-4">
      {open ? (
        <form
          className="space-y-3"
          onSubmit={async (e) => {
            e.preventDefault();
            const res = await change.run();
            if (res) {
              setUser(res.user);
              setOpen(false);
              onChanged();
            }
          }}
        >
          <Field
            label="Rätt e-postadress"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={change.error?.fields.email}
          />
          {change.error && !change.error.fields.email && <ErrorMessage error={change.error} />}
          <div className="flex flex-wrap gap-2">
            <Button type="submit" pending={change.pending}>
              Spara och skicka ny länk
            </Button>
            <Button type="button" variant="ghost" onClick={() => setOpen(false)}>
              Avbryt
            </Button>
          </div>
        </form>
      ) : (
        <p className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
          <button
            type="button"
            className="font-semibold text-night-700 underline"
            onClick={() => setOpen(true)}
          >
            Fel e-postadress? Ändra den
          </button>
          <button
            type="button"
            className="text-muted underline"
            onClick={async () => {
              await logout();
              navigate('/');
            }}
          >
            Logga ut
          </button>
        </p>
      )}
    </div>
  );
}

export function ConfirmEmailChangePage() {
  useTitle('Bekräfta ny e-postadress');
  const [params] = useSearchParams();
  const token = params.get('token');
  const { user, refresh } = useAuth();
  const [state, setState] = useState<{ status: 'idle' | 'ok' | 'error'; message?: string; email?: string }>({
    status: 'idle',
  });
  const started = useRef(false);
  useEffect(() => {
    if (!token || started.current) return;
    started.current = true;
    api
      .post<{ email: string }>('/api/auth/confirm-email-change', { token })
      .then(async (res) => {
        setState({ status: 'ok', email: res.email });
        await refresh();
      })
      .catch((e) => setState({ status: 'error', message: e.message }));
  }, [token, refresh]);
  return (
    <Container narrow>
      <PageTitle>Ny e-postadress</PageTitle>
      <Card className="space-y-4">
        {!token && <Alert>Länken saknar kod.</Alert>}
        {token && state.status === 'idle' && <Loading />}
        {state.status === 'ok' && (
          <>
            <Alert kind="success">
              Klart! Du loggar nu in med <strong>{state.email}</strong>.
            </Alert>
            <Link
              to={user ? '/konto' : '/logga-in'}
              className="inline-block font-semibold text-night-700 underline"
            >
              {user ? 'Till Mitt konto' : 'Logga in'}
            </Link>
          </>
        )}
        {state.status === 'error' && <Alert>{state.message}</Alert>}
      </Card>
    </Container>
  );
}

export function ForgotPasswordPage() {
  useTitle('Glömt lösenord');
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const action = useAction(() => api.post('/api/auth/forgot-password', { email }));
  return (
    <Container narrow>
      <PageTitle>Glömt lösenordet?</PageTitle>
      <Card>
        {sent ? (
          <Alert kind="success">
            Om det finns ett konto för {email} har vi skickat en länk för att välja nytt lösenord. Länken
            gäller i en timme.
          </Alert>
        ) : (
          <form
            className="space-y-4"
            onSubmit={async (e) => {
              e.preventDefault();
              if (await action.run()) setSent(true);
            }}
          >
            <p className="text-muted">Ange e-postadressen du registrerade dig med så skickar vi en länk.</p>
            <ErrorMessage error={action.error} />
            <Field
              label="E-postadress"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            <Button type="submit" pending={action.pending}>
              Skicka länk
            </Button>
          </form>
        )}
      </Card>
    </Container>
  );
}

export function ResetPasswordPage() {
  useTitle('Nytt lösenord');
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const [password, setPassword] = useState('');
  const [repeat, setRepeat] = useState('');
  const [done, setDone] = useState(false);
  const action = useAction(() => api.post('/api/auth/reset-password', { token, password }));
  const mismatch = repeat.length > 0 && repeat !== password;
  return (
    <Container narrow>
      <PageTitle>Välj nytt lösenord</PageTitle>
      <Card>
        {done ? (
          <div className="space-y-4">
            <Alert kind="success">Ditt lösenord är ändrat. Du har loggats ut från andra enheter.</Alert>
            <Link to="/logga-in" className="font-semibold text-night-700 underline">
              Logga in
            </Link>
          </div>
        ) : (
          <form
            className="space-y-4"
            onSubmit={async (e) => {
              e.preventDefault();
              if (mismatch) return;
              if (await action.run()) setDone(true);
            }}
          >
            {!token && <Alert>Länken saknar kod. Be om en ny länk.</Alert>}
            <ErrorMessage error={action.error} />
            <Field
              label="Nytt lösenord"
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              hint={`Minst ${PASSWORD_MIN_LENGTH} tecken.`}
              error={action.error?.fields.password}
            />
            <Field
              label="Upprepa lösenordet"
              type="password"
              autoComplete="new-password"
              value={repeat}
              onChange={(e) => setRepeat(e.target.value)}
              error={mismatch ? 'Lösenorden är olika.' : undefined}
            />
            <Button type="submit" pending={action.pending} disabled={!token || mismatch}>
              Spara lösenord
            </Button>
          </form>
        )}
      </Card>
    </Container>
  );
}

export function UnsubscribePage() {
  useTitle('Avregistrera');
  const [params] = useSearchParams();
  const token = params.get('token');
  const { user } = useAuth();
  const unsubscribe = useAction(() => api.post('/api/unsubscribe', { token }));
  const [done, setDone] = useState(false);

  return (
    <Container narrow>
      <PageTitle>Påminnelser och utskick</PageTitle>
      <Card className="space-y-4">
        {done ? (
          <>
            <Alert kind="success">
              Klart! Du får inte längre påminnelser och utskick från kursen. Mejl om ditt konto, rättningar
              och intyg skickas fortfarande.
            </Alert>
            <p className="text-sm text-muted">
              Ångrar du dig kan du slå på utskicken igen under{' '}
              <Link to="/konto" className="font-semibold text-night-700 underline">
                Mitt konto
              </Link>
              .
            </p>
          </>
        ) : token ? (
          <>
            <p>Vill du sluta få påminnelser och utskick från kursens lärare?</p>
            <ErrorMessage error={unsubscribe.error} />
            <Button
              pending={unsubscribe.pending}
              onClick={async () => {
                if (await unsubscribe.run()) setDone(true);
              }}
            >
              Avregistrera mig
            </Button>
          </>
        ) : (
          <p>
            Länken saknar uppgifter. {user ? 'Du kan ändra dina utskick under ' : 'Logga in och ändra under '}
            <Link to="/konto" className="font-semibold text-night-700 underline">
              Mitt konto
            </Link>
            .
          </p>
        )}
      </Card>
    </Container>
  );
}
